'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { useImperativeHandle, useRef } from 'react';
import {
  EmptyObject,
  ErrorOption,
  FieldValues,
  FormProvider,
  SubmitHandler,
  UseFormProps,
  UseFormReturn,
  useForm,
  useFormState,
} from 'react-hook-form';
import { ZodType } from 'zod';

import { trackClick } from '@sola/tracking/components/datalayer.js';
import { getTextFromReactNode } from '@sola/utils/react.js';

import {
  ButtonStyle,
  ButtonStyleProps,
} from '../../atoms/button-style/button-style';
import { Alert } from '../alert/alert';

/**
 * Form's internal methods to be publically exposed to parent.
 */
export interface FormPublicMethods extends Pick<
  UseFormReturn,
  | 'clearErrors'
  | 'formState'
  | 'getFieldState'
  | 'getValues'
  | 'reset'
  | 'resetField'
  | 'setError'
  | 'setValue'
  | 'trigger'
  | 'watch'
> {
  setGeneralError: (
    error: ErrorOption,
    config?: { shouldFocus: boolean },
  ) => void;
}

/**
 * Function that creates a ref to be passed to a Form's formMethods prop.
 * This allows for parent components to have access to a form's internal methods.
 */
export const useFormMethods = () => useRef<FormPublicMethods | null>(null);

export const FormError: React.FC = () => {
  const { errors } = useFormState();
  if (!errors?.root) {
    return null;
  }
  return (
    <div className="Form__error my-comfy-sm">
      <Alert
        variant="negative"
        dismissible={false}
        message={errors.root.error.message}
      />
    </div>
  );
};

export interface FormProps extends Omit<
  React.ComponentPropsWithoutRef<'form'>,
  'onSubmit'
> {
  /**
   * Ref for internal <form> element.
   */
  formRef?: React.Ref<HTMLFormElement> | null;
  /**
   * Ref that will be assigned form's internal methods and state.
   */
  formMethods?: React.Ref<EmptyObject | FormPublicMethods>;
  isCustomError?: boolean;
  onSubmit: SubmitHandler<FieldValues>;
  /**
   * React hook form useForm options.
   */
  options?: Omit<UseFormProps, 'resolver' | 'context'>;
  validationSchema?: ZodType;
}
/**
 * Form component.
 * It is recommended to landmark your form to provide an accessible name
 * You can do this by either providing aria-labelledby, aria-label, or <title>
 */
export const Form = ({
  children,
  formRef,
  formMethods,
  isCustomError,
  onSubmit,
  options,
  validationSchema,
  ...formProps
}: FormProps) => {
  const methods = useForm({
    ...(validationSchema && {
      resolver: zodResolver(validationSchema),
    }),
    mode: 'onBlur',
    shouldUnregister: true,
    ...options,
  });
  /**
   * Set all of the public methods we want to expose to consumers.
   */
  useImperativeHandle(formMethods, () => {
    return {
      clearErrors: methods.clearErrors,
      // TODO This only makes the initial state available. Will need to build solution to
      // enable parent component to read from form's internal state.
      formState: methods.formState,
      getFieldState: methods.getFieldState,
      getValues: methods.getValues,
      register: methods.register,
      reset: methods.reset,
      resetField: methods.resetField,
      setError: methods.setError,
      setGeneralError: (error, config = undefined) => {
        methods.setError('root.error', error, config);
      },
      setValue: methods.setValue,
      trigger: methods.trigger,
      unregister: methods.unregister,
      watch: methods.watch,
    };
  }, []);

  return (
    <FormProvider {...methods}>
      {!isCustomError && <FormError />}
      <form
        ref={formRef}
        noValidate
        onSubmit={methods.handleSubmit(onSubmit)}
        className="flex flex-col gap-comfy-sm"
        {...formProps}
      >
        {children}
      </form>
    </FormProvider>
  );
};

export interface FormSubmitProps extends ButtonStyleProps {
  children: React.ReactNode;
  className?: string;
  disableUntilFilled?: boolean;
}
/**
 * Form's submission button, which has access to internal state
 */
export const FormSubmit: React.FC<FormSubmitProps> = ({
  children,
  disabled,
  disableUntilFilled,
  ...buttonProps
}) => {
  const { isValid } = useFormState();
  let isDisabled = disabled;

  if (!isDisabled) {
    isDisabled = !disableUntilFilled ? false : !isValid;
  }

  return (
    <ButtonStyle fullWidth {...buttonProps} disabled={isDisabled}>
      <button
        onClick={() => {
          trackClick(getTextFromReactNode(children) || 'Form Submit');
        }}
        type="submit"
      >
        {children}
      </button>
    </ButtonStyle>
  );
};
